import React, { useEffect, useRef } from "react";

/*
 * Subtle WebGL shader behind the sign-in brand card: slow, domain-warped
 * cobalt noise that drifts and bends toward the pointer. It only renders while
 * the card is hovered (plus a short fade-out), so an idle sign-in page costs no
 * GPU, and the canvas stays hidden whenever it isn't drawing. No WebGL or
 * reduced motion → renders nothing; the static card remains.
 */

const VERT = `attribute vec2 p; void main() { gl_Position = vec4(p, 0.0, 1.0); }`;

const FRAG = `
precision mediump float;
uniform vec2 uRes;
uniform vec2 uMouse;   // px, origin top-left
uniform float uTime;
uniform float uMix;    // 0..1 hover fade

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++) { v += a * noise(p); p *= 2.0; a *= 0.5; }
  return v;
}

void main() {
  vec2 frag = vec2(gl_FragCoord.x, uRes.y - gl_FragCoord.y);
  float s = min(uRes.x, uRes.y);
  vec2 uv = frag / s;
  vec2 m = uMouse / s;
  float t = uTime * 0.06;

  // pointer pulls the field toward itself
  vec2 toM = m - uv;
  float pull = exp(-dot(toM, toM) * 6.0);
  vec2 q = uv * 2.2 + toM * pull * 0.6;

  vec2 w = vec2(fbm(q + t), fbm(q + vec2(5.2, 1.3) - t));
  float n = fbm(q + 2.0 * w + vec2(t * 0.7, -t * 0.4));

  // cobalt (#494FDF) → lavender (#8487E4) bands, brightest near the pointer
  vec3 cobalt = vec3(0.286, 0.31, 0.875);
  vec3 lav = vec3(0.518, 0.529, 0.894);
  vec3 col = mix(cobalt, lav, smoothstep(0.45, 0.85, n));
  float glow = smoothstep(0.35, 0.9, n) * (0.35 + 0.65 * pull);

  float a = glow * 0.32 * uMix;
  gl_FragColor = vec4(col * a, a);   // premultiplied
}`;

export default function BrandShader() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const card = canvas && canvas.parentElement;
    if (!card || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const gl = canvas.getContext("webgl", { premultipliedAlpha: true, antialias: false });
    if (!gl) return undefined;

    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      return sh;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return undefined;
    gl.useProgram(prog);
    gl.clearColor(0, 0, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const u = (n) => gl.getUniformLocation(prog, n);
    const uRes = u("uRes"), uMouse = u("uMouse"), uTime = u("uTime"), uMix = u("uMix");

    // Half resolution: the field is soft, and this keeps the fragment cost low.
    const scale = Math.min(window.devicePixelRatio || 1, 2) * 0.5;
    const resize = () => {
      canvas.width = Math.max(1, Math.round(card.clientWidth * scale));
      canvas.height = Math.max(1, Math.round(card.clientHeight * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(card);

    const target = { x: 0, y: 0 };
    const mouse = { x: 0, y: 0 };
    let hovered = false;
    let mix = 0;
    let raf = 0;
    const start = performance.now();

    const frame = (now) => {
      mix += ((hovered ? 1 : 0) - mix) * 0.06;
      mouse.x += (target.x - mouse.x) * 0.08;   // eased follow
      mouse.y += (target.y - mouse.y) * 0.08;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform2f(uMouse, mouse.x * scale, mouse.y * scale);
      gl.uniform1f(uTime, (now - start) / 1000);
      gl.uniform1f(uMix, mix);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = !hovered && mix < 0.01 ? 0 : requestAnimationFrame(frame);
      if (!raf) canvas.classList.remove("is-live");
    };
    const move = (e) => {
      const r = card.getBoundingClientRect();
      target.x = e.clientX - r.left;
      target.y = e.clientY - r.top;
    };
    const enter = (e) => {
      move(e);
      if (!hovered && mix < 0.01) { mouse.x = target.x; mouse.y = target.y; }
      hovered = true;
      canvas.classList.add("is-live");
      if (!raf) raf = requestAnimationFrame(frame);
    };
    const leave = () => { hovered = false; };

    card.addEventListener("pointerenter", enter);
    card.addEventListener("pointermove", move);
    card.addEventListener("pointerleave", leave);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      card.removeEventListener("pointerenter", enter);
      card.removeEventListener("pointermove", move);
      card.removeEventListener("pointerleave", leave);
      const lose = gl.getExtension("WEBGL_lose_context");
      if (lose) lose.loseContext();
    };
  }, []);

  return <canvas ref={ref} className="brand-shader" aria-hidden="true" />;
}
